import api from 'api/api'
import cn from 'classnames'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import WithTooltip from 'components/shared/WithTooltip'
import React, { useRef, useState, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'
import useStore from 'state/knovStore'
import styles from '../answer.module.scss'
import { OrdinalInfoPopup } from './OrdinalInfoPopup'
import { confirmAsync } from 'components/shared/confirmUtils'
import { useBsvExchangeRate } from 'api/useBsvExchangeRate'
import { SATOSHIS } from 'lib/bsv-util'
import { useMintOrdinalWorkflowStatus } from 'hooks/useMintOrdinalWorkflowStatus'

/**
 * Check if the answer's image was pasted in, in which case we can use the local link and not go to the server.
 * @param {import('types/answers').Answer} answer - The answer object to check
 * @returns {boolean} - Whether the answer has a blob URL
 */
const hasBlob = answer => {
    const url = answer?.answer_image_url
    return typeof url === 'string' && (url.startsWith('blob:') || url.startsWith('data:'))
}

/**
 * Component that displays an answer's image with associated controls and overlays
 * @type {React.ForwardRefExoticComponent<{
 *   answer: import('types/answers').Answer,
 *   draftAnswer?: import('types/answers').Answer,
 *   toggle: () => void,
 *   isThin?: boolean,
 *   showDownload?: boolean,
 *   isEmbed?: boolean,
 *   isTitle?: boolean,
 *   isActive?: boolean,
 *   path?: string,
 *   imageSize?: string,
 *   uiId?: string,
 *   hasSeenGenerating?: boolean,
 *   contextStyles?: object
 * } & React.RefAttributes<HTMLImageElement>>}
 */
const AnswerImage = React.forwardRef((props, ref) => {
    const [hasImageError, setHasImageError] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isOrdinalInfoVisible, setIsOrdinalInfoVisible] = useState(false)
    const [mintingWorkflowId, setMintingWorkflowId] = useState(null)
    const [mintingRunId, setMintingRunId] = useState(null)
    const [mintingCompleted, setMintingCompleted] = useState(false)
    const ordinalButtonRef = useRef(null)

    const DEBUG = useStore(state => state.DEBUG)
    const ordinalsEnabled = true //useUserHasFeature('ordinals_wallet')
    const openOrdinalsModal = useStore(state => state.actions.modalActions.openOrdinalsModal)
    const answer = props.answer
    const draftAnswer = props.draftAnswer
    const isThin = props?.isThin || false
    let url

    const bsvExchangeRate = useBsvExchangeRate()

    // Temporal workflow status tracking for ordinal minting with EventBus support
    const {
        isPolling: isMintingInProgress,
        startPolling: startMintingPolling,
        error: mintingError,
    } = useMintOrdinalWorkflowStatus(mintingWorkflowId, mintingRunId, {
        enabled: !!(mintingWorkflowId && mintingRunId),
        pollInterval: 2000, // Slower polling since EventBus provides real-time updates
        onComplete: result => {
            console.log('🎉 Mint ordinal completed via EventBus:', result)
            // Signal completion via state change, not direct modal opening
            setMintingCompleted(true)
            setIsLoading(false) // Stop spinner
        },
        onError: error => {
            console.error('Minting failed:', error)
            setIsLoading(false)
            setMintingWorkflowId(null)
            setMintingRunId(null)
            alert(`Failed to mint ordinal: ${error}`)
        },
    })

    // Start polling when workflow IDs are set
    useEffect(() => {
        if (mintingWorkflowId && mintingRunId && !isMintingInProgress) {
            startMintingPolling()
        }
    }, [mintingWorkflowId, mintingRunId, startMintingPolling, isMintingInProgress])

    // Handle minting completion in main component context (not hook callback)
    useEffect(() => {
        if (mintingCompleted) {
            const handleCompletion = async () => {
                setIsLoading(false)
                setMintingWorkflowId(null)
                setMintingRunId(null)
                setMintingCompleted(false)

                const confRes = await confirmAsync('Ordinal minted successfully, view in wallet?')
                if (confRes) {
                    // TODO: should open directly to the minted ordinal in the ordinal wallet
                    openOrdinalsModal()
                }
            }
            handleCompletion()
        }
    }, [mintingCompleted, openOrdinalsModal])

    if (hasBlob(draftAnswer)) {
        url = draftAnswer.answer_image_url
    } else if (hasBlob(answer)) {
        url = answer.answer_image_url
    } else {
        url = `${window.knovApiUrl}/api/v1/answers/${answer?.id}/image`
        const imageSize = props.imageSize

        let imageQueryString = `?updated_at=${parseInt(new Date(answer.updated_at).getTime())}`
        if (imageSize) {
            imageQueryString = imageQueryString
                ? `${imageQueryString}&size=${imageSize}`
                : `?size=${imageSize}`
        }
        if (hasImageError) {
            imageQueryString = imageQueryString
                ? `${imageQueryString}&break_cache=true`
                : '?break_cache=true'
        }
        url = `${url}${imageQueryString}`
    }

    const isActive = props.isActive
    const payloadContainerStyles = isMobile
        ? styles.mobilePayloadContainer
        : styles.payloadContainer

    const streamPayloadContainerStyles = isActive ? null : styles.streamPayloadContainer
    const downloadStyles = props.showDownload ? { display: 'flex' } : { display: 'none' }
    const embedImageStyles = props.isEmbed ? styles.embedImagePayload : null

    let name
    const path = props.path
    if (path) {
        const arr = path.split('/')
        name = arr[arr.length - 1]
    } else {
        name = 'knov'
    }

    const matchingBsvAttachment = answer?.bsv_attachments?.find(
        att =>
            att.attachment_url == answer.answer_image_url ||
            answer.image_urls?.some(img => img.attachment_id === att.attachment_id),
    )

    const renderBsvAttachmentStatus = matchingBsvAttachment => {
        const bsvTx = matchingBsvAttachment?.bsv_tx
        const txId = bsvTx?.tx_id

        const isAwaitingResubmission =
            bsvTx && !txId && (new Date() - new Date(bsvTx.created_at)) / 1000 > 5

        if (isAwaitingResubmission) {
            const retryInfo = bsvTx.retries > 0 ? ` (${bsvTx.retries} retries)` : ''
            return (
                <WithTooltip
                    tip={`Treechat wasn't able to post this onchain immediately but will try again on the next block ${retryInfo}`}
                >
                    <i className="fa fa-clock-o" />
                </WithTooltip>
            )
        }

        if (!txId) {
            return (
                <WithTooltip tip="Transaction pending">
                    <i className="fa fa-spinner fa-spin" />
                </WithTooltip>
            )
        }

        const isUnconfirmed = !bsvTx?.confirmed

        return (
            <WithTooltip tip={`${isUnconfirmed ? 'Unconfirmed' : 'Confirmed'} tx: ${txId}`}>
                <a
                    href={`https://whatsonchain.com/tx/${txId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={ev => ev.stopPropagation()}
                >
                    <i className={`fa fa-cube`} />
                </a>
            </WithTooltip>
        )
    }

    const isAnswerOwner = answer?.user_id === window.gon?.currentUser?.id

    const handleMintOrdinal = async ev => {
        ev.stopPropagation()
        if (!matchingBsvAttachment) return

        // TODO: get mint price from server
        const mintPriceUsd = 0.1
        const mintPriceSats = Math.round((mintPriceUsd / bsvExchangeRate) * SATOSHIS)

        const confirmMintRes = await confirmAsync(
            `Mint this ordinal for $${mintPriceUsd.toFixed(
                2,
            )} USD (${mintPriceSats.toLocaleString()} sats)?`,
        )

        if (!confirmMintRes) return

        setIsLoading(true)

        try {
            const mintResult = await api.mintOrdinalTemporal(answer?.id, {
                treechat_fee_amount_sats: mintPriceSats,
            })
            const { workflow_id: workflowId, run_id: runId } = mintResult

            if (workflowId && runId) {
                // Set workflow IDs to enable status polling - completion handled by useEffect
                setMintingWorkflowId(workflowId)
                setMintingRunId(runId)
            } else {
                throw new Error('Failed to start mint ordinal workflow')
            }
        } catch (error) {
            alert(`Failed to mint ordinal: ${error}`)
            console.error('Failed to mint ordinal:', error)
            setIsLoading(false)
        }
    }

    const handleDownload = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (window.KNOVIGATOR_IS_MOBILE) {
            // Mobile download - use direct link
            window.open(`${window.knovApiUrl}/api/v1/answers/${answer?.id}/download_image`)
        } else {
            // Desktop download - fetch and trigger download
            fetch(`${window.knovApiUrl}/api/v1/answers/${answer?.id}/download_image`)
                .then(response => response.blob())
                .then(blob => {
                    const url = window.URL.createObjectURL(blob)

                    const a = document.createElement('a')
                    a.style.display = 'none'
                    a.href = url
                    a.download = name || 'image'
                    document.body.appendChild(a)
                    a.click()

                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                })
                .catch(err => {
                    console.error('Download failed:', err)
                })
        }
    }

    // Lock size only if generation has started and an image error occurs, using placeholder dimensions
    const applyFixedSize = props.hasSeenGenerating && props.placeholderSize

    // Container style: lock width/height on error
    const containerStyle = {
        contain: 'paint',
        ...(applyFixedSize && {
            aspectRatio: `${props.placeholderSize.width} / ${props.placeholderSize.height}`,
        }),
    }
    // Image style: lock width/height on error
    const imageStyle = {
        objectFit: 'contain',
        ...(applyFixedSize && {
            aspectRatio: `${props.placeholderSize.width} / ${props.placeholderSize.height}`,
        }),
    }

    return (
        <>
            {url && (
                <div
                    style={containerStyle}
                    className={cn(
                        payloadContainerStyles,
                        props.isTitle && styles.titlePayloadContainer,
                        styles.imagePayloadContainer,
                        streamPayloadContainerStyles,
                        embedImageStyles,
                        props.contextStyles,
                    )}
                >
                    <img
                        ref={ref}
                        style={imageStyle}
                        width={answer?.image_width}
                        height={answer?.image_height}
                        onClick={ev => {
                            ev.preventDefault()
                            ev.stopPropagation()
                            props.toggle()
                        }}
                        src={url}
                        loading="lazy"
                        data-zoomable={props.uiId}
                        onError={e => {
                            setHasImageError(true)
                        }}
                    />
                    {!isThin &&
                        (matchingBsvAttachment ||
                            answer?.image_bsv_ordinal?.id ||
                            props.showDownload) && (
                            <div className={styles.imageStatusContainer}>
                                {answer?.image_bsv_ordinal?.id && (
                                    <div
                                        className={styles.ordinalButtonOverlay}
                                        onClick={ev => {
                                            ev.stopPropagation()
                                            setIsOrdinalInfoVisible(!isOrdinalInfoVisible)
                                        }}
                                        ref={ordinalButtonRef}
                                    >
                                        <OrdinalsIcon size={20} />
                                        {isOrdinalInfoVisible && (
                                            <OrdinalInfoPopup
                                                ordinal={answer.image_bsv_ordinal}
                                                onClose={() => setIsOrdinalInfoVisible(false)}
                                                openOrdinalsModal={openOrdinalsModal}
                                                buttonRef={ordinalButtonRef}
                                            />
                                        )}
                                    </div>
                                )}
                                {matchingBsvAttachment && (
                                    <div className={styles.bsvStatusOverlay}>
                                        {renderBsvAttachmentStatus(matchingBsvAttachment)}
                                        {false && DEBUG && (
                                            <pre style={{ width: '200px' }}>
                                                {JSON.stringify(matchingBsvAttachment, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                )}
                                {props.showDownload && (
                                    <div className={styles.downloadButtonOverlay}>
                                        <WithTooltip tip="Download image">
                                            {window.KNOVIGATOR_IS_MOBILE ? (
                                                <a
                                                    href={`${window.knovApiUrl}/api/v1/answers/${answer?.id}/download_image`}
                                                    download={name}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={ev => {
                                                        ev.stopPropagation()
                                                    }}
                                                >
                                                    <i className="fa fa-download" />
                                                </a>
                                            ) : (
                                                <i
                                                    className="fa fa-download"
                                                    onClick={handleDownload}
                                                />
                                            )}
                                        </WithTooltip>
                                    </div>
                                )}
                            </div>
                        )}
                    {!isThin && (
                        <>
                            {ordinalsEnabled &&
                            isAnswerOwner &&
                            matchingBsvAttachment &&
                            !answer?.image_bsv_ordinal?.id ? (
                                <div className={styles.mintButtonOverlay}>
                                    <WithTooltip
                                        tip={
                                            isMintingInProgress
                                                ? 'Minting in progress...'
                                                : 'Mint as NFT'
                                        }
                                    >
                                        <div
                                            className={cn(styles.mintButton, {
                                                [styles.loading]: isLoading || isMintingInProgress,
                                            })}
                                            onClick={
                                                isLoading || isMintingInProgress
                                                    ? undefined
                                                    : handleMintOrdinal
                                            }
                                            style={{
                                                pointerEvents:
                                                    isLoading || isMintingInProgress
                                                        ? 'none'
                                                        : 'auto',
                                                opacity: isLoading || isMintingInProgress ? 0.7 : 1,
                                            }}
                                        >
                                            {isLoading || isMintingInProgress ? (
                                                <>
                                                    <span className={styles.buttonIcon}>
                                                        <i className="fa fa-spinner fa-spin" />
                                                    </span>
                                                    MINT
                                                </>
                                            ) : (
                                                <>
                                                    <span className={styles.buttonIcon}>
                                                        <OrdinalsIcon size={16} />
                                                    </span>
                                                    MINT
                                                </>
                                            )}
                                        </div>
                                    </WithTooltip>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            )}
        </>
    )
})

export default AnswerImage
